// src/hooks/dashboard/queryBuilders/dashboardQueryBuilders.ts

import {
  fetchDashboardCurrent,
  fetchDashboardCurrentRatios,
  fetchDashboardCurrentRollups,
  fetchDashboardCurrentChanges,
  DASHBOARD_WINDOWS,
} from "@/services/queries/dashboards/currentDashboardQueries";
import {
  adaptMetricsRows,
  adaptRatiosRows,
  adaptPercentageChangesRows,
  adaptRollupsRows,
} from "@/utils/dashboard/dashboardDataAdapter";
import type { WindowsResponse } from "@/types/dashboards/dashboardCurrent";

/**
 * Merges two arrays of rows by token symbol/name
 */
function mergeRowsByToken(
  primary: Array<Record<string, unknown>>,
  secondary: Array<Record<string, unknown>>
): Array<Record<string, unknown>> {
  const byKey = new Map<string, Record<string, unknown>>();
  const getKey = (r: Record<string, unknown>) => 
    String(r["tokenSymbol"] || r["tokenName"] || "");
  
  for (const r of primary) {
    const key = getKey(r);
    byKey.set(key, { ...(byKey.get(key) || {}), ...r });
  }
  
  for (const r of secondary) {
    const key = getKey(r);
    byKey.set(key, { ...(byKey.get(key) || {}), ...r });
  }
  
  return Array.from(byKey.values());
}

/**
 * Builds query function for metrics data (current + rollups)
 */
export async function buildMetricsQueryFn(categoryId: number, signal?: AbortSignal) {
  const [currentResp, rollupsResp] = await Promise.all([
    fetchDashboardCurrent({ categoryId, ...(signal && { signal }) }),
    fetchDashboardCurrentRollups({ 
      categoryId, 
      windowsCsv: DASHBOARD_WINDOWS.join(","),
      ...(signal && { signal })
    }),
  ]);

  const data = adaptMetricsRows(currentResp, rollupsResp);
  return { data, lastUpdated: new Date().toISOString() };
}

/**
 * Builds query function for current data (metrics + ratios, no rollups)
 */
export async function buildCurrentQueryFn(categoryId: number, signal?: AbortSignal) {
  const [currentResp, ratiosResp] = await Promise.all([
    fetchDashboardCurrent({ categoryId, ...(signal && { signal }) }),
    fetchDashboardCurrentRatios({ categoryId, ...(signal && { signal }) }),
  ]);

  const emptyWindows: WindowsResponse = { category_id: categoryId, tokens: [] };
  const currentRows = adaptMetricsRows(currentResp, emptyWindows);
  const ratioRows = adaptRatiosRows(ratiosResp);

  const data = mergeRowsByToken(
    currentRows as unknown as Record<string, unknown>[],
    ratioRows as unknown as Record<string, unknown>[]
  );
  
  return { data, lastUpdated: new Date().toISOString() };
}

/**
 * Builds query function for ratios data only
 */
export async function buildRatiosQueryFn(categoryId: number, signal?: AbortSignal) {
  const ratiosResp = await fetchDashboardCurrentRatios({ 
    categoryId, 
    ...(signal && { signal }) 
  });
  const data = adaptRatiosRows(ratiosResp);
  return { data, lastUpdated: new Date().toISOString() };
}

/**
 * Builds query function for percentage changes data
 */
export async function buildPercentageChangesQueryFn(categoryId: number, signal?: AbortSignal) {
  const changesResp = await fetchDashboardCurrentChanges({ 
    categoryId, 
    windowsCsv: DASHBOARD_WINDOWS.join(","),
    ...(signal && { signal })
  });
  
  const data = adaptPercentageChangesRows(changesResp);
  return { data, lastUpdated: new Date().toISOString() };
}

/**
 * Builds query function for rollups data (aggregates only)
 */
export async function buildRollupsQueryFn(categoryId: number, signal?: AbortSignal) {
  const rollupsResp = await fetchDashboardCurrentRollups({ 
    categoryId, 
    windowsCsv: DASHBOARD_WINDOWS.join(","),
    ...(signal && { signal })
  });
  
  const data = adaptRollupsRows(rollupsResp);
  return { data, lastUpdated: new Date().toISOString() };
}

